/**
 * StoryDirector — the helper content scenes use to touch story state.
 *
 * - `flag(f)`   advance the main line (only the legal next flag); the context autosaves on `story:flag`.
 * - `mark(m)`   set an optional side mark (`seen:*`, `talked:*`, `done:*`).
 * - `when(f)`   gating: true once `f` has been reached (flags are a prefix of the route).
 * - `beatFor(scene, resumeFlag)` resume logic: which beat a content scene (re)starts at.
 *
 * Phaser-free. Build with `StoryDirector.fromContext(ctx)` in scenes or with plain deps in tests.
 */
import { flagIndex, isStoryFlag, StoryState, type StoryFlagId } from './flags';
import type { GameContext } from '@/core/context';

export type ContentSceneKey = 'Prologue' | 'GreenHarbor' | 'TurtleInn';

export interface BeatEntry {
  scene: ContentSceneKey;
  /** Flag the scene was entered with (`SceneEntryData.resumeFlag`); null = brand new game. */
  resumeFlag: StoryFlagId | null;
  /** Beat id the scene should (re)start. `handoff` = the scene does not own this flag: call ctx.enterRoute(). */
  beat: string;
  /** Main-line flag this beat sets when it finishes (null = beat ends by leaving the scene). */
  sets: StoryFlagId | null;
  note: string;
}

/**
 * Resume table (documented contract for chapter builders). A refresh/load never resumes mid-cinematic:
 * the beat that produces the *next* flag is restarted from its beginning.
 *
 * | scene       | resumeFlag             | beat                   | sets                  |
 * |-------------|------------------------|------------------------|-----------------------|
 * | Prologue    | (null) / TITLE         | letter-open            | LETTER_OPEN           |
 * | Prologue    | LETTER_OPEN            | memory-storm           | MEMORY_STORM          |
 * | Prologue    | MEMORY_STORM           | memory-island          | MEMORY_ISLAND_RITUAL  |
 * | Prologue    | MEMORY_ISLAND_RITUAL   | memory-black-water     | MEMORY_BLACK_WATER    |
 * | Prologue    | MEMORY_BLACK_WATER     | memory-farewell        | MEMORY_FAREWELL       |
 * | Prologue    | MEMORY_FAREWELL        | memory-sword-training  | MEMORY_SWORD_TRAINING |
 * | Prologue    | MEMORY_SWORD_TRAINING  | letter-seal            | LETTER_SENT           |
 * | Prologue    | LETTER_SENT            | prophecy               | PROPHECY_REVEAL       |
 * | GreenHarbor | PROPHECY_REVEAL        | harbor-intro           | HARBOR_FREE_ROAM      |
 * | GreenHarbor | HARBOR_FREE_ROAM       | free-roam              | MERCHANT_TURTLE_EGG   |
 * | GreenHarbor | MERCHANT_TURTLE_EGG    | rain-approach          | RAIN_APPROACHES       |
 * | GreenHarbor | RAIN_APPROACHES        | to-inn                 | — (exit → TurtleInn)  |
 * | TurtleInn   | RAIN_APPROACHES        | inn-enter              | INN_ARGUMENT          |
 * | TurtleInn   | INN_ARGUMENT           | omen                   | OMEN_END              |
 * | any         | anything else          | handoff                | —                     |
 */
export const BEAT_TABLE: BeatEntry[] = [
  { scene: 'Prologue', resumeFlag: null, beat: 'letter-open', sets: 'LETTER_OPEN', note: '码头边坐下写信；信纸展开' },
  { scene: 'Prologue', resumeFlag: 'TITLE', beat: 'letter-open', sets: 'LETTER_OPEN', note: '码头边坐下写信；信纸展开' },
  { scene: 'Prologue', resumeFlag: 'LETTER_OPEN', beat: 'memory-storm', sets: 'MEMORY_STORM', note: '黑夜风浪回忆' },
  { scene: 'Prologue', resumeFlag: 'MEMORY_STORM', beat: 'memory-island', sets: 'MEMORY_ISLAND_RITUAL', note: '无名小岛与法阵' },
  { scene: 'Prologue', resumeFlag: 'MEMORY_ISLAND_RITUAL', beat: 'memory-black-water', sets: 'MEMORY_BLACK_WATER', note: '星塘患病与黑水' },
  { scene: 'Prologue', resumeFlag: 'MEMORY_BLACK_WATER', beat: 'memory-farewell', sets: 'MEMORY_FAREWELL', note: '父亲的决定与告别' },
  { scene: 'Prologue', resumeFlag: 'MEMORY_FAREWELL', beat: 'memory-sword-training', sets: 'MEMORY_SWORD_TRAINING', note: '与赵呈博同行、剑术教学战' },
  { scene: 'Prologue', resumeFlag: 'MEMORY_SWORD_TRAINING', beat: 'letter-seal', sets: 'LETTER_SENT', note: '封蜡、装瓶、交给返航者' },
  { scene: 'Prologue', resumeFlag: 'LETTER_SENT', beat: 'prophecy', sets: 'PROPHECY_REVEAL', note: '赵呈博的思索（视角切换）' },
  { scene: 'GreenHarbor', resumeFlag: 'PROPHECY_REVEAL', beat: 'harbor-intro', sets: 'HARBOR_FREE_ROAM', note: '“老师，你在想什么呢？”→“走吧”' },
  { scene: 'GreenHarbor', resumeFlag: 'HARBOR_FREE_ROAM', beat: 'free-roam', sets: 'MERCHANT_TURTLE_EGG', note: '自由探索；亚北商贩对话' },
  { scene: 'GreenHarbor', resumeFlag: 'MERCHANT_TURTLE_EGG', beat: 'rain-approach', sets: 'RAIN_APPROACHES', note: '天色转阴；“一会儿就下雨了”' },
  { scene: 'GreenHarbor', resumeFlag: 'RAIN_APPROACHES', beat: 'to-inn', sets: null, note: '走向旅店入口（出口 → TurtleInn）' },
  { scene: 'TurtleInn', resumeFlag: 'RAIN_APPROACHES', beat: 'inn-enter', sets: 'INN_ARGUMENT', note: '挤入、落座、苦酒；争论演出' },
  { scene: 'TurtleInn', resumeFlag: 'INN_ARGUMENT', beat: 'omen', sets: 'OMEN_END', note: '不祥预感；定格于赵呈博注视' },
];

export const HANDOFF_BEAT = 'handoff';

export interface BeatResolution { beat: string; sets: StoryFlagId | null; handoff: boolean }

/** Which beat a content scene should (re)start for the flag it was entered with. */
export function resolveBeat(scene: ContentSceneKey, resumeFlag: string | null | undefined): BeatResolution {
  const rf = resumeFlag && isStoryFlag(resumeFlag) ? resumeFlag : null;
  const entry = BEAT_TABLE.find((b) => b.scene === scene && b.resumeFlag === rf);
  if (entry) return { beat: entry.beat, sets: entry.sets, handoff: false };
  return { beat: HANDOFF_BEAT, sets: null, handoff: true };
}

export interface StoryDirectorDeps {
  story: StoryState;
  /**
   * Called after a flag is newly set. Leave it out when the story state emits `story:flag` — GameContext
   * already autosaves on that event (`fromContext` relies on it, so a beat writes one save, not two).
   */
  autosave?: () => void;
}

export class StoryDirector {
  constructor(private readonly deps: StoryDirectorDeps) {}

  /** Bound to the shared context: `ctx.story` emits `story:flag`, on which GameContext autosaves once. */
  static fromContext(ctx: GameContext): StoryDirector {
    return new StoryDirector({ story: ctx.story });
  }

  get story(): StoryState { return this.deps.story; }

  /** Advance the main line. Returns true only when the flag was newly set (then autosaves). */
  flag(flag: string): boolean {
    if (!isStoryFlag(flag)) { console.warn(`[story] unknown flag "${flag}"`); return false; }
    const ok = this.deps.story.advance(flag);
    if (ok) this.deps.autosave?.();
    return ok;
  }

  mark(mark: string): boolean { return this.deps.story.mark(mark); }
  hasMark(mark: string): boolean { return this.deps.story.hasMark(mark); }

  /** Gating: true once `flag` has been reached (current index >= flag index). */
  when(flag: string): boolean {
    if (!isStoryFlag(flag)) return false;
    return this.deps.story.currentIndex() >= flagIndex(flag);
  }
  has(flag: string): boolean { return this.when(flag); }
  /** True while `flag` has not been reached yet. */
  before(flag: string): boolean { return !this.when(flag); }
  /** True from `from` (inclusive) until `until` is set. */
  between(from: string, until: string): boolean { return this.when(from) && !this.when(until); }
  isNext(flag: string): boolean { return this.deps.story.next() === flag; }
  current(): StoryFlagId | null { return this.deps.story.current(); }
  next(): StoryFlagId | null { return this.deps.story.next(); }

  /** Resume logic: beat to (re)start for a scene. Defaults `resumeFlag` to the current flag. */
  beatFor(scene: ContentSceneKey, resumeFlag?: string | null): BeatResolution {
    return resolveBeat(scene, resumeFlag === undefined ? this.deps.story.current() : resumeFlag);
  }
}
